/**
 * Contact — public contact page.
 *
 * Audience: anyone — software providers, hardware providers, or end users —
 * with a question that doesn't have a dedicated page. Restyled to share the
 * Home/Product visual system (lg cards, brand-gradient CTA band). There's no
 * self-serve form — enquiries route to email — so the page makes the contact
 * routes obvious.
 *
 * Nav + footer live in PublicLayout; this page only declares its content.
 */

import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  IconArrowRight,
  IconMail,
  IconBuildingStore,
  IconBook,
} from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';

const CONTACT_EMAIL = 'info@simkura.com';

// ── Layout primitives ─────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 clamp(20px, 6vw, 64px);
`;

const Section = styled.section`
  padding: clamp(40px, 7vh, 80px) 0;
`;

// ── Hero — washed text hero, matching Home. No hero card, no hero image.

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(56px, 11vh, 120px) 0 clamp(32px, 5vh, 56px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(60% 80% at 20% 0%, #d8e4f0 0%, transparent 60%),
      radial-gradient(50% 70% at 85% 10%, #dddcea 0%, transparent 55%),
      radial-gradient(40% 50% at 60% 100%, #c9d6df22 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => '#0d253d'};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const HeroHeadline = styled.h1`
  font-size: clamp(32px, 5vw, 56px);
  line-height: 1.05;
  letter-spacing: -0.025em;
  font-weight: 700;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  max-width: 15ch;
`;

const HeroSub = styled.p`
  font-size: clamp(16px, 2vw, 19px);
  line-height: 1.55;
  color: ${({ theme }) => theme.font.color.secondary};
  margin: 0;
  max-width: 46ch;
`;

// ── Contact method cards ──────────────────────────────────────────────────────

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(5)};
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(7)};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  display: flex;
  flex-direction: column;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => '#d8e4f0'};
    color: ${({ theme }) => '#0d253d'};
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }
  h2 {
    font-size: 18px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0 0 ${({ theme }) => theme.spacing(2)} 0;
  }
  p {
    margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
    font-size: 14px;
    line-height: 1.55;
    color: ${({ theme }) => theme.font.color.secondary};
    flex: 1;
  }
`;

const cardLinkStyles = `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

const CardLink = styled.a`
  ${cardLinkStyles}
  color: ${({ theme }) => '#0d253d'};
`;

const CardRouteLink = styled(Link)`
  ${cardLinkStyles}
  color: ${({ theme }) => '#0d253d'};
`;

// ── Closing CTA band ──────────────────────────────────────────────────────────

const CtaBand = styled.div`
  text-align: center;
  padding: clamp(44px, 7vh, 76px) 24px;
  border-radius: ${({ theme }) => theme.border.radius.xl};
  background: linear-gradient(135deg, #1b3a5e 0%, #0d253d 100%);
  color: #fff;

  h2 {
    font-size: clamp(24px, 4vw, 36px);
    color: #fff;
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  }
  p {
    color: rgba(255, 255, 255, 0.88);
    font-size: 17px;
    line-height: 1.55;
    margin: 0 auto ${({ theme }) => theme.spacing(7)};
    max-width: 50ch;
  }
`;

const CtaWhite = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 50px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: #fff;
  color: ${({ theme }) => '#0d253d'};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

// ── Page ──────────────────────────────────────────────────────────────────────

function Contact() {
  return (
    <PublicLayout>
      {/* Hero — washed text hero */}
      <HeroSection>
        <Container>
          <Hero>
            <Eyebrow>Contact</Eyebrow>
            <HeroHeadline>Get in touch.</HeroHeadline>
            <HeroSub>
              Software provider, hardware provider, or just have a question
              about the locks? Tell us what you need and we'll point you to
              the right place.
            </HeroSub>
          </Hero>
        </Container>
      </HeroSection>

      {/* Contact methods */}
      <Section>
        <Container>
          <CardGrid>
            <Card>
              <span className="icon"><IconMail size={20} /></span>
              <h2>Email us</h2>
              <p>For partnership enquiries, integration support, or general questions.</p>
              <CardLink href={`mailto:${CONTACT_EMAIL}`}>
                {CONTACT_EMAIL} <IconArrowRight size={14} />
              </CardLink>
            </Card>

            <Card>
              <span className="icon"><IconBuildingStore size={20} /></span>
              <h2>Where to buy</h2>
              <p>Simkura locks are sold and supported by our partners. See who carries them and get pricing.</p>
              <CardRouteLink to="/hardware">
                Find a partner <IconArrowRight size={14} />
              </CardRouteLink>
            </Card>

            <Card>
              <span className="icon"><IconBook size={20} /></span>
              <h2>Integration docs</h2>
              <p>The full API reference, webhook payloads, and guides are public at docs.simkura.com — no account needed.</p>
              <CardLink href="https://docs.simkura.com" target="_blank" rel="noreferrer">
                Read the docs <IconArrowRight size={14} />
              </CardLink>
            </Card>
          </CardGrid>
        </Container>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Container>
          <CtaBand>
            <h2>Looking for Simkura locks?</h2>
            <p>Simkura is sold and supported by our partner network. Find a partner to get pricing and put Simkura on your doors.</p>
            <CtaWhite to="/hardware">
              Where to buy
              <IconArrowRight size={18} />
            </CtaWhite>
          </CtaBand>
        </Container>
      </Section>
    </PublicLayout>
  );
}

export default Contact;
